import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { CommunityAmenities } from '../components/CommunityAmenities';
import { CTASection } from '../components/CTASection';
import { ExploreMoreSection } from '../components/ExploreMoreSection';
import { LeadForm } from '../components/LeadForm';
import { MeetingModal } from '../components/MeetingModal';
import { ResponsiveHeroImage } from '../components/ResponsiveHeroImage';
import { Button } from '../components/ui/button';
import { Calendar, Phone } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

export const Amenities = () => {
  const navigate = useNavigate();
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);

  const whatsappNumber = '13451234567';
  const whatsappMessage = encodeURIComponent(
    'Hi, I\'m interested in The Pearl at Grand Harbour amenities. Can you provide more information?'
  );

  const handleScheduleTour = () => {
    setMeetingOpen(true);
  };

  const handleContact = () => {
    setLeadFormOpen(true);
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <NavBar 
        onRegisterClick={() => setLeadFormOpen(true)} 
        onConsultClick={() => setMeetingOpen(true)}
      />

      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <ResponsiveHeroImage
            baseName="hero-lagoon"
            originalSrc="/img/hero-amenity.jpg"
            alt="Luxury community amenities"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-end">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="max-w-4xl">
              <h1 className="font-display text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Your World Curated
              </h1>
              <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed">
                We believe a home is more than its walls—it’s the life it invites. From the quiet expanse of the lagoon to the vibrant pulse of the plaza, each amenity weaves into the rhythm of your life at The Pearl.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-brand-teal hover:bg-brand-navy text-white"
                  onClick={handleScheduleTour}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule a Tour
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white bg-black/30 hover:bg-white hover:!text-foreground hover:!border-foreground"
                  onClick={handleContact}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Showcase */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <CommunityAmenities showAll={true} />
        </div>
      </section>

      {/* Lifestyle Benefits Section */}
      <section className="py-16 lg:py-24 bg-soft-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-6">
                A Lifestyle Beyond Compare
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Our amenities are more than just features – they're the foundation of a lifestyle that celebrates luxury, wellness, and community connection.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Wellness Focus */}
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-3xl">🧘‍♀️</span>
                </div>
                <h3 className="font-semibold text-xl text-foreground">Wellness & Fitness</h3>
                <p className="text-muted-foreground">
                  State-of-the-art fitness facilities, serene lagoon views, and wellness programs designed to nurture both body and mind.
                </p>
              </div>

              {/* Community Connection */}
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-3xl">🤝</span>
                </div>
                <h3 className="font-semibold text-xl text-foreground">Community Connection</h3>
                <p className="text-muted-foreground">
                  Shared spaces and activities that bring neighbors together, creating lasting friendships and a true sense of community.
                </p>
              </div>

              {/* Luxury Living */}
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-3xl">✨</span>
                </div>
                <h3 className="font-semibold text-xl text-foreground">Luxury Living</h3>
                <p className="text-muted-foreground">
                  Every detail crafted to exceed expectations, from premium finishes to concierge services that anticipate your every need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore More Section */}
      <ExploreMoreSection 
        onContactClick={() => setLeadFormOpen(true)}
        currentPage="amenities"
      />

      {/* CTA Section */}
      <CTASection 
        onRegisterClick={() => setLeadFormOpen(true)}
        onConsultClick={() => setMeetingOpen(true)}
        onWhatsAppClick={handleWhatsAppClick}
      />

      {/* Footer */}
      <Footer />

      {/* Modals */}
      <LeadForm open={leadFormOpen} onOpenChange={setLeadFormOpen} />
      <MeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        onWhatsAppClick={handleWhatsAppClick}
      />
    </div>
  );
};

